import { useMemo } from "react";
import { useTrips } from "@/hooks/useTrips";
import TripCard from "./TripCard";
import { Skeleton } from "@/components/ui/skeleton";
import { TripDisplay } from "@/lib/trips";

interface TripsSectionProps {
  searchTerm?: string;
}

const TripsSection = ({ searchTerm = "" }: TripsSectionProps) => {
  const { data: trips, isLoading, error } = useTrips();

  // Filter trips based on search criteria
  const filteredTrips = useMemo(() => {
    if (!trips) return [];

    return trips.filter((trip: TripDisplay) => {
      // Search term filter
      const matchesSearch = !searchTerm || 
        trip.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
        trip.destination.toLowerCase().includes(searchTerm.toLowerCase()) ||
        trip.description.toLowerCase().includes(searchTerm.toLowerCase()) ||
        trip.highlights.some(h => h.toLowerCase().includes(searchTerm.toLowerCase()));

      return matchesSearch;
    });
  }, [trips, searchTerm]);

  return (
    <section id="trips" className="py-20 md:py-28 bg-gradient-sunset">
      <div className="container mx-auto px-4">
        <div className="text-center mb-12 md:mb-16">
          <span className="inline-block px-4 py-2 rounded-full bg-primary/10 text-primary text-sm font-medium mb-4">
            Featured Trips
          </span>
          <h2 className="font-display text-3xl md:text-5xl font-bold text-foreground mb-4">
            Your Next Adventure Awaits
          </h2>
          <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
            Curated group trips with 20 compatible singles (10 men, 10 women). 
            Every trip includes expert guides, accommodation, and organic moments designed for connection.
          </p>
        </div>

        {isLoading ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8">
            {[1, 2, 3].map((i) => (
              <div key={i} className="space-y-4">
                <Skeleton className="h-56 w-full rounded-lg" />
                <Skeleton className="h-4 w-3/4" />
                <Skeleton className="h-4 w-1/2" />
              </div>
            ))}
          </div>
        ) : error ? (
          <div className="text-center text-destructive">
            Failed to load trips. Please try again later.
          </div>
        ) : filteredTrips.length === 0 ? (
          <div className="text-center py-16">
            <p className="text-lg text-muted-foreground mb-2">No trips found matching your search criteria.</p>
            <p className="text-sm text-muted-foreground">Try adjusting your filters or search terms.</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8">
            {filteredTrips.map((trip, index) => (
              <TripCard key={trip.id} trip={trip} index={index} />
            ))}
          </div>
        )}
      </div>
    </section>
  );
};

export default TripsSection;
